/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: hooks/usePinListList.ts
 🗓️ Created: 2025-01-27
 🧩 Description:
    Hook for managing PIN Lists list state and data fetching.
────────────────────────────────────────────────────────────────*/

import { useCallback, useEffect } from 'react';
import { useNotifications } from '../../../hooks/useNotifications';
import { useI18n } from '../../../hooks/useI18n';
import { fetchPinLists } from '../api/pinListsApi';
import { ListPinListsParams, PinList } from '../types';

interface UsePinListListOptions {
  domainId?: number;
  onRefreshNeeded?: () => void;
}

/**
 * Hook for PIN Lists list management
 */
export function usePinListList(options: UsePinListListOptions = {}) {
  const { domainId, onRefreshNeeded } = options;
  const { showError } = useNotifications();
  const { t } = useI18n();

  const fetchData = useCallback(async (params: Omit<ListPinListsParams, 'domainId'>) => {
    try {
      const result = await fetchPinLists({
        ...params,
        domainId
      });

      const data: PinList[] = Array.isArray(result.data) ? result.data : [];
      const pagination = result.pagination || { page: 0, limit: 0, total: 0, totalPages: 0 };

      return {
        data,
        pagination: {
          page: pagination.page ?? params.page,
          limit: pagination.limit ?? params.limit,
          total: pagination.total ?? data.length,
          totalPages: pagination.totalPages ?? 0
        }
      };
    } catch (error) {
      const message = error instanceof Error ? error.message : t('pinLists.messages.loadError');
      showError(message);
      throw error;
    }
  }, [showError, t, domainId]);

  // Trigger refresh when domainId changes
  useEffect(() => {
    if (domainId !== undefined && onRefreshNeeded) {
      onRefreshNeeded();
    }
  }, [domainId]);

  return {
    fetchData,
    domainId
  };
}

